import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, BookOpen, Sparkles } from 'lucide-react';
import { useAiPrayerAssistant } from '@/hooks/useAiPrayerAssistant';
import { useAiPrayerAnalytics } from '@/hooks/useAiPrayerAnalytics';
import { useSmartPrayerFeatures } from '@/hooks/useSmartPrayerFeatures';
import { usePrayerChoice } from '@/contexts/PrayerChoiceContext';
import { LAYERS } from '@/utils/layers';
import PrayerSuggestions from './PrayerSuggestions';
import AbielAvatar from '@/components/ai-prayer/ai-assistant/components/AbielAvatar';

import type { ChoiceContext } from '@/types/prayerChoice';

interface PrayerChoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onINeedPrayer: () => void;
  onIWantToPray: () => void;
  prayerContext?: ChoiceContext;
}

export default function PrayerChoiceDialog({
  isOpen,
  onClose,
  onINeedPrayer,
  onIWantToPray,
  prayerContext
}: PrayerChoiceDialogProps) {
  const { settings } = useAiPrayerAssistant();
  const { trackAction } = useAiPrayerAnalytics();
  const { suggestions, streak, seasonalTheme, communityRequests, isLoading } = useSmartPrayerFeatures();
  const { openAiAssistant } = usePrayerChoice();
  
  const [avatar, setAvatar] = useState<string>('/lovable-uploads/ddc323f5-0cb6-4357-b5de-60a1da3a5d45.png');
  const [isINeedPrayerLoading, setIsINeedPrayerLoading] = useState(false);
  const [isIWantToPrayLoading, setIsIWantToPrayLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (settings?.avatar_url) {
      setAvatar(settings.avatar_url);
    } else {
      setAvatar('/lovable-uploads/ddc323f5-0cb6-4357-b5de-60a1da3a5d45.png');
    }
  }, [settings?.avatar_url]);

  const handleImageError = () => {
    setAvatar('/lovable-uploads/ddc323f5-0cb6-4357-b5de-60a1da3a5d45.png');
  };

  const handleINeedPrayerClick = async () => {
    setIsINeedPrayerLoading(true);
    trackAction('prayer_requested', { source: 'choice_dialog' });
    try {
      // First call the analytics handler
      await onINeedPrayer();
      // Then open the AI Prayer Assistant directly
      openAiAssistant();
      // Close the choice dialog
      onClose();
    } finally {
      setIsINeedPrayerLoading(false);
    }
  };

  const handleIWantToPrayClick = async () => {
    setIsIWantToPrayLoading(true);
    trackAction('prayer_generated', { source: 'choice_dialog', method: 'manual' });
    try {
      await onIWantToPray();
    } finally {
      setIsIWantToPrayLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    trackAction('prayer_requested', { 
      source: 'suggestion', 
      category: suggestion.category,
      suggestion_id: suggestion.id 
    });
    // Automatically select "I need prayer" for suggestions
    handleINeedPrayerClick();
  };

  const handleCommunityRequestClick = (request: any) => {
    trackAction('prayer_requested', { 
      source: 'community_request', 
      request_id: request.id 
    });
    // Automatically select "I need prayer" for community requests
    handleINeedPrayerClick();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md w-full max-w-lg mx-auto p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-gold/20 shadow-2xl"
        style={{ zIndex: LAYERS.PRAYER_CHOICE }}
      >
        {/* Enhanced Header with seasonal gradient */}
        <div className={`relative bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 p-4 sm:p-6 overflow-hidden`}>
          {/* Subtle animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-gold/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-gold/30 rounded-full blur-lg animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img 
                  src="/lovable-uploads/5c9fd9ce-1698-4252-ae42-ed43fba9511e.png"
                  alt="HolyGround Logo"
                  className="h-6 w-6 sm:h-8 sm:w-8 object-contain relative z-10"
                />
                <div className="absolute inset-0 bg-gold/30 rounded-full blur-md animate-pulse"></div>
              </div>
              <div>
                <h2 className="text-gold font-bold text-base sm:text-lg tracking-wide">HOLYGROUND</h2>
                <p className="text-gold/80 text-xs sm:text-sm font-medium">Abiel</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-gold/70 hover:text-gold hover:bg-gold/10 rounded-full transition-all duration-200 hover:scale-105"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Enhanced Content Section with glass morphism */}
        <div className="bg-gradient-to-b from-white/95 to-white/90 backdrop-blur-sm p-6 sm:p-8 text-center max-h-[500px] overflow-y-auto">
          {/* Enhanced Avatar with breathing animation */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="relative group">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-gold/40 shadow-xl relative z-10 group-hover:border-gold/60 transition-all duration-300">
                <AbielAvatar 
                  size="lg"
                  className="w-full h-full object-cover animate-pulse"
                />
              </div>
              {/* Breathing glow effect */}
              <div className="absolute inset-0 rounded-full bg-gold/20 blur-xl animate-pulse group-hover:bg-gold/30 transition-all duration-300"></div>
              <div className="absolute inset-2 rounded-full bg-gold/10 blur-lg animate-pulse delay-500 group-hover:bg-gold/20 transition-all duration-300"></div>
              {/* Sparkle effect */}
              <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-gold animate-pulse" />
            </div>
          </div>

          {/* Enhanced Typography */}
          <h3 className="text-gray-900 font-bold text-xl sm:text-2xl mb-4 sm:mb-6 px-2 leading-tight">
            {prayerContext && 'type' in prayerContext && prayerContext.type === 'bible_verse' 
              ? 'Pray About This Scripture' 
              : 'Spiritually-Guided Prayer, Powered by AI'}
          </h3>

          {/* Enhanced Bible Verse Context Display */}
          {prayerContext && 'type' in prayerContext && prayerContext.type === 'bible_verse' && (
            <div className="bg-gradient-to-r from-gold/5 to-gold/10 border-l-4 border-gold p-6 mb-6 text-left rounded-r-lg shadow-inner backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="h-5 w-5 text-gold" />
                <p className="text-gold font-semibold text-sm">{prayerContext.reference}</p>
              </div>
              <p className="text-gray-900 text-sm italic leading-relaxed mb-3">"{prayerContext.text}"</p>
              <p className="text-gray-600 text-xs font-medium">— {prayerContext.version}</p>
            </div>
          )}

          {/* Enhanced Action Buttons */}
          <div className="space-y-4 sm:space-y-5">
            <Button
              onClick={handleINeedPrayerClick}
              disabled={isINeedPrayerLoading}
              className="w-full py-6 sm:py-7 text-base sm:text-lg font-bold bg-gradient-to-r from-gold to-gold/90 hover:from-gold/90 hover:to-gold text-white border-2 border-gold hover:border-gold/80"
              style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
            >
              {isINeedPrayerLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Connecting...
                </div>
              ) : (
                "Ask Abiel For Prayer Guidance"
              )}
            </Button>

            <Button
              onClick={handleIWantToPrayClick}
              disabled={isIWantToPrayLoading}
              className="w-full py-6 sm:py-7 text-base sm:text-lg font-bold bg-white/90 backdrop-blur-sm border-2 border-gold text-gray-900 hover:bg-gradient-to-r hover:from-gold hover:to-gold/90 hover:text-white transition-all duration-200"
            >
              {isIWantToPrayLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-gray-400/30 border-t-gray-900 rounded-full animate-spin"></div>
                  Opening...
                </div>
              ) : (
                "Write My Own Prayer"
              )}
            </Button>
          </div>

          {/* Show/Hide Smart Features Button */}
          <div className="mt-6 sm:mt-8">
            <Button
              onClick={() => setShowSuggestions(!showSuggestions)}
              variant="ghost"
              className="w-full text-sm text-gray-600 hover:text-gray-900 hover:bg-gold/5 transition-all duration-200"
            >
              {showSuggestions ? 'Hide' : 'Show'} Smart Suggestions & Features
              <Sparkles className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Smart Prayer Features */}
          {showSuggestions && (
            <div className="mt-4 border-t border-gold/20 pt-4">
              <PrayerSuggestions
                suggestions={suggestions}
                streak={streak}
                communityRequests={communityRequests}
                onSuggestionClick={handleSuggestionClick}
                onCommunityRequestClick={handleCommunityRequestClick}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Enhanced Helper Text with seasonal message */}
          <p className="text-gray-600 mt-6 text-center text-sm sm:text-base px-2 leading-relaxed">
            {seasonalTheme?.message || (prayerContext && 'type' in prayerContext && prayerContext.type === 'bible_verse' 
              ? 'How would you like to pray about this scripture?' 
              : 'How would you like Abiel to guide you in prayer today?')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
